import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ClipboardText, LinkSimple, PaperPlaneRight } from "@phosphor-icons/react";

const STAGES = [
  { key: "new", title: "1 · New applications", desc: "Do the pre-screen: open the form, answer it with them (or about them), and pick a recommendation — that sends the right email automatically." },
  { key: "interview_scheduled", title: "2 · Interview scheduled", desc: "They have a date and time. When they come in, open the interview form and complete it." },
  { key: "fast_tracked", title: "2 · Fast tracked — checks in progress", desc: "Reference and employer checks went out at prescreen. The face-to-face interview still has to happen — the offer goes out only after it." },
  { key: "offer_pending", title: "3 · Offer sent — waiting for signature", desc: "They have the offer-letter link. Their staff account and portal login are created the moment they sign." },
  { key: "hired", title: "4 · Hired — onboarding", desc: "Account created. Track documents, disclosures, and orientation on their employee record." },
  { key: "closed", title: "Not moving forward", desc: "Deferred (maybe later) or declined.", keys: ["deferred", "declined"] },
];

const STAGE_PILL = {
  new: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]",
  interview_scheduled: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]",
  fast_tracked: "border-[#27AE60]/40 bg-[#27AE60]/15 text-[#1e8449]",
  offer_pending: "border-primary/40 bg-primary/10 text-primary",
  hired: "border-primary/40 bg-primary/10 text-primary",
  deferred: "border-stone-300 bg-stone-100 text-stone-500",
  declined: "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]",
};

const NEXT_ACTION = {
  new: { label: "Do the pre-screen now", to: (a) => `/hiring/prescreen/${a.id}` },
  interview_scheduled: { label: "Do the interview", to: (a) => `/hiring/interview/${a.id}` },
  fast_tracked: { label: "Do the interview", to: (a) => `/hiring/interview/${a.id}` },
  hired: { label: "Open onboarding", to: (a) => (a.hired_user_id ? `/team/${a.hired_user_id}` : null) },
};

const WAITING = {
  offer_pending: "Waiting on them — nothing for you to do until they sign. Resend the offer if it's been a couple of days.",
  deferred: "On hold — nothing due unless you reopen them.",
  declined: "Closed — nothing due.",
};

function ApplicantCard({ a, busy, resend, onOpen }) {
  const na = NEXT_ACTION[a.status] || {};
  const to = na.to ? na.to(a) : null;
  return (
    <div onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen()}
      className="cursor-pointer rounded-xl border border-stone-200 bg-white p-5 shadow-sm transition-all hover:border-primary/40 hover:shadow-md"
      data-testid={`applicant-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-heading text-base font-semibold text-stone-900">{a.name}</p>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STAGE_PILL[a.status] || STAGE_PILL.new}`} data-testid={`applicant-status-${a.id}`}>{a.status.replace(/_/g, " ")}</span>
            {a.referral && <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-stone-500" data-testid={`applicant-referral-${a.id}`}>{a.referral}</span>}
          </div>
          <p className="mt-0.5 text-xs text-stone-400">{a.position} · applied {(a.created_at || "").slice(0, 10)} · {a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
          {a.interview_at && (a.status === "interview_scheduled" || a.status === "fast_tracked") && (
            <p className="mt-1 text-xs font-semibold text-[#1e8449]" data-testid={`applicant-interview-${a.id}`}>Interview: {a.interview_at}</p>
          )}
          {a.prescreen && (
            <p className="mt-1 text-xs text-stone-500" data-testid={`applicant-prescreen-summary-${a.id}`}>
              Pre-screened {a.prescreen.completed_at ? `on ${a.prescreen.completed_at.slice(0, 10)} ` : ""}by {a.prescreen.screener || "office"} ·{" "}
              <b>{{"yes": "Yes — interview", "fast_track": "FAST TRACK", "no_90": "No — 90 days", "no_decline": "No — declined"}[a.prescreen.recommendation] || (a.prescreen.recommendation || "").replace(/_/g, " ")}</b>
            </p>
          )}
          {a.interview && (
            <p className="mt-1 text-xs text-stone-500" data-testid={`applicant-interview-done-${a.id}`}>
              Interviewed {a.interview.interview_date || ""} by {a.interview.conducted_by || "—"} · <b>{a.status === "hired" ? "hired ✓" : a.status.replace(/_/g, " ")}</b>
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {to && (
            <Link to={to} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-stone-900"
              data-testid={`applicant-action-${a.id}`}>
              {na.label} →
            </Link>
          )}
          {!to && WAITING[a.status] && (
            <span className="max-w-56 text-right text-xs font-medium text-stone-400" data-testid={`applicant-waiting-${a.id}`}>{WAITING[a.status]}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HiringPage() {
  const [applicants, setApplicants] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "" });
  const [selected, setSelected] = useState(null);
  const [invites, setInvites] = useState([]);

  const load = () => {
    api.get("/applicants").then((r) => setApplicants(r.data.applicants || [])).catch((e) => toast.error(formatApiError(e)));
    api.get("/hiring/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/applicants/invites").then((r) => setInvites(r.data.invites || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put("/hiring/settings", {
        prescreen_email: settings.prescreen_email,
        interview_email: settings.interview_email,
        interview_instructions: settings.interview_instructions,
        positions: (settings.positionsText ?? settings.positions.join("\n")).split("\n").map((p) => p.trim()).filter(Boolean),
      });
      toast.success("Hiring settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const resend = async (a, kind) => {
    setBusy(true);
    try {
      await api.post(`/applicants/${a.id}/resend-email/${kind}`);
      toast.success(`${kind === "offer" ? "Offer" : "Interview confirmation"} email resent to ${a.email}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const copyText = (url, msg) => {
    try {
      if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    } catch {}
    toast.success(msg, { description: url, duration: 8000 });
  };
  const copyIndeedLink = () => copyText(`${window.location.origin}/apply?utm_source=indeed&utm_campaign=dcw`, "Indeed application link copied — or copy it from the toast below");

  const sendInvite = async () => {
    setBusy(true);
    try {
      await api.post("/applicants/send-application", invite);
      toast.success(`Application link emailed to ${invite.name}`);
      setSendOpen(false);
      setInvite({ name: "", email: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const decide = async (a, action) => {
    setBusy(true);
    try {
      await api.put(`/applicants/${a.id}/decision`, { action });
      toast.success(action === "defer" ? `${a.name} moved to "not moving forward — maybe later"` : `${a.name} declined`);
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const removeApplicant = async (a) => {
    if (!window.confirm(`Delete ${a.name}'s application completely? This also removes any hired account for them.`)) return;
    setBusy(true);
    try {
      await api.delete(`/applicants/${a.id}`);
      toast.success(`${a.name} deleted`);
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const byStage = (stage) => (applicants || []).filter((a) => (stage.keys || [stage.key]).includes(a.status));

  return (
    <div className="max-w-5xl space-y-8" data-testid="hiring-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="hiring-title">Hiring pipeline</h1>
          <p className="mt-1 max-w-xl text-sm text-stone-500">
            Every applicant moves left to right: application → pre-screen → interview → offer → hired.
            Each group below is one step, with the next action right on the card.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setSendOpen(true)} data-testid="hiring-send-application"
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-white">
            <PaperPlaneRight size={15} /> Send application
          </button>
          <button onClick={copyIndeedLink} data-testid="hiring-copy-indeed-link"
            className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-stone-600 transition-colors hover:bg-stone-900 hover:text-white">
            <LinkSimple size={15} /> Copy Indeed link
          </button>
        </div>
      </div>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="send-application-dialog">
          <DialogHeader><DialogTitle>Send the application</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-500">They'll get an email inviting them to complete the Direct Care Worker application. It lands in this pipeline the moment they submit.</p>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Their first & last name</label>
              <Input value={invite.name} onChange={(e) => setInvite((v) => ({ ...v, name: e.target.value }))} className="border-stone-300" data-testid="send-application-name" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Their email</label>
              <Input type="email" value={invite.email} onChange={(e) => setInvite((v) => ({ ...v, email: e.target.value }))} className="border-stone-300" data-testid="send-application-email" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <button onClick={() => copyText(`${window.location.origin}/apply`, "Application link copied — or copy it from the toast below")}
                className="text-xs font-semibold text-primary hover:underline" data-testid="send-application-copy-instead">…or just copy the link</button>
              <Button onClick={sendInvite} disabled={busy || !invite.name.trim() || !invite.email.trim()}
                className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="send-application-submit">
                <PaperPlaneRight size={15} className="mr-1.5" /> {busy ? "Sending…" : "Send the invite"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="applicant-detail-dialog">
          {selected && (() => {
            const a = selected;
            const na = NEXT_ACTION[a.status] || {};
            const to = na.to ? na.to(a) : null;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex flex-wrap items-center gap-2">
                    {a.name}
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STAGE_PILL[a.status] || STAGE_PILL.new}`}>{a.status.replace(/_/g, " ")}</span>
                  </DialogTitle>
                </DialogHeader>

                <div className="rounded-lg border border-primary/25 bg-primary/5 p-4" data-testid="applicant-detail-next">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Your next step with {a.name.split(" ")[0]}</p>
                  {to ? (
                    <Link to={to} onClick={() => setSelected(null)}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:bg-stone-900"
                      data-testid="applicant-detail-action">
                      {na.label} →
                    </Link>
                  ) : (
                    <p className="mt-1 text-sm text-stone-600">{WAITING[a.status] || "Nothing due right now."}</p>
                  )}
                  {a.status === "offer_pending" && a.offer?.token && (
                    <a href={`/offer/${a.offer.token}`} target="_blank" rel="noreferrer"
                      className="mt-2 inline-block text-xs font-semibold text-primary hover:underline" data-testid="applicant-detail-offer-link">
                      View their offer page (what they see) →
                    </a>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-stone-200 p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Contact</p>
                    <p className="mt-1.5 text-sm text-stone-700"><a className="text-primary hover:underline" href={`mailto:${a.email}`}>{a.email}</a></p>
                    {a.phone && <p className="text-sm text-stone-700"><a className="text-primary hover:underline" href={`tel:${a.phone}`}>{a.phone}</a></p>}
                    {(a.address || a.city) && <p className="mt-1 text-sm text-stone-500">{[a.address, a.city, a.state, a.zip].filter(Boolean).join(", ")}</p>}
                    {a.referral && <p className="mt-1 text-xs text-stone-400">Found us via: {a.referral}</p>}
                  </div>
                  <div className="rounded-lg border border-stone-200 p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Availability & experience</p>
                    {(a.availability || []).length > 0 && <p className="mt-1.5 text-sm text-stone-700">{a.availability.join(", ")}</p>}
                    {a.experience && <p className="mt-1 text-sm text-stone-600">{a.experience}</p>}
                    {(a.certifications || []).length > 0 && <p className="mt-1 text-xs text-stone-500">Certifications: {a.certifications.join(", ")}</p>}
                  </div>
                </div>

                {(a.employers || []).length > 0 && (
                  <div className="rounded-lg border border-stone-200 p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Work history</p>
                    {a.employers.map((e, i) => (
                      <p key={i} className="mt-1.5 text-sm text-stone-700">{e.employer} — {e.position}{e.start_date ? ` (${e.start_date} → ${e.end_date || "now"})` : ""}</p>
                    ))}
                  </div>
                )}
                {(a.references || []).length > 0 && (
                  <div className="rounded-lg border border-stone-200 p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">References they listed</p>
                    {a.references.map((r, i) => (
                      <p key={i} className="mt-1.5 text-sm text-stone-700">{r.name} — {r.relationship}{r.years_known ? `, ${r.years_known} yrs` : ""}{r.phone ? ` · ${r.phone}` : ""}</p>
                    ))}
                  </div>
                )}

                {(a.prescreen || a.interview || a.offer) && (
                  <div className="rounded-lg border border-stone-200 bg-accent/40 p-4" data-testid="applicant-detail-history">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Process so far</p>
                    {a.prescreen && <p className="mt-1.5 text-sm text-stone-700">Pre-screen {a.prescreen.completed_at?.slice(0, 10)} by {a.prescreen.screener || "office"} — <b>{(a.prescreen.recommendation || "").replace(/_/g, " ")}</b></p>}
                    {a.interview && <p className="mt-1 text-sm text-stone-700">Interview {a.interview.interview_date} by {a.interview.conducted_by || "—"}</p>}
                    {a.offer && <p className="mt-1 text-sm text-stone-700">Offer: {a.offer.position} at ${a.offer.pay_rate}/hr, sent {(a.offer.prepared_at || "").slice(0, 10)}{a.offer.signed?.signed_at ? ` — signed ${a.offer.signed.signed_at.slice(0, 10)} ✓` : " — not signed yet"}</p>}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3 border-t border-stone-100 pt-4">
                  {a.status === "interview_scheduled" && (
                    <button onClick={() => resend(a, "interview")} disabled={busy} className="text-xs font-semibold text-primary hover:underline" data-testid="applicant-detail-resend-interview">Resend interview email</button>
                  )}
                  {a.status === "offer_pending" && (
                    <button onClick={() => resend(a, "offer")} disabled={busy} className="text-xs font-semibold text-primary hover:underline" data-testid="applicant-detail-resend-offer">Resend offer email</button>
                  )}
                  {(a.status === "new" || a.status === "interview_scheduled" || a.status === "fast_tracked") && (
                    <>
                      <button onClick={() => decide(a, "defer")} disabled={busy} className="text-xs font-semibold text-stone-500 hover:underline" data-testid="applicant-detail-defer">Hold for later</button>
                      <button onClick={() => decide(a, "decline")} disabled={busy} className="text-xs font-semibold text-stone-500 hover:underline" data-testid="applicant-detail-decline">Decline</button>
                    </>
                  )}
                  <button onClick={() => removeApplicant(a)} disabled={busy} className="ml-auto text-xs font-semibold text-[#C0392B] hover:underline" data-testid="applicant-detail-delete">Delete application</button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {applicants === null ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : applicants.length === 0 ? (
        <p className="rounded-xl border border-dashed border-stone-300 bg-white p-12 text-center text-sm text-stone-400" data-testid="hiring-empty">
          No applications yet. Share the application link: <code className="rounded bg-accent px-2 py-0.5 text-xs">{window.location.origin}/apply</code>
        </p>
      ) : (
        STAGES.map((stage) => {
          const list = byStage(stage);
          if (!list.length && stage.key === "closed") return null;
          return (
            <section key={stage.key} data-testid={`stage-${stage.key}`}>
              <div className="mb-3 flex items-baseline gap-3">
                <h2 className="font-heading text-lg font-semibold text-stone-900">{stage.title}</h2>
                <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-bold text-stone-500" data-testid={`stage-count-${stage.key}`}>{list.length}</span>
              </div>
              <p className="mb-3 text-xs text-stone-500">{stage.desc}</p>
              {list.length === 0 ? (
                <p className="rounded-lg border border-dashed border-stone-200 px-4 py-3 text-xs text-stone-400">Nobody at this step right now.</p>
              ) : (
                <div className="space-y-3">
                  {list.map((a) => <ApplicantCard key={a.id} a={a} busy={busy} resend={resend} onOpen={() => setSelected(a)} />)}
                </div>
              )}
            </section>
          );
        })
      )}

      {invites.length > 0 && (
        <div className="mt-8 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="invites-log">
          <p className="font-heading text-sm font-semibold text-stone-900">Application links sent ({invites.length})</p>
          <p className="mt-0.5 text-xs text-stone-500">Every "send application" email — and whether it turned into an application.</p>
          <div className="mt-3 divide-y divide-stone-100">
            {invites.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center gap-2 py-2 text-xs" data-testid={`invite-${i.id}`}>
                <span className="font-medium text-stone-800">{i.name}</span>
                <span className="text-stone-400">{i.email}</span>
                <span className="text-stone-300">sent {(i.sent_at || "").slice(0, 10)} by {i.sent_by}</span>
                {i.status === "applied" ? (
                  <button onClick={() => { const a = (applicants || []).find((x) => x.id === i.applicant_id); if (a) setSelected(a); }}
                    className="ml-auto rounded-full bg-[#27AE60]/10 px-2 py-0.5 font-bold text-[#1e8449] hover:bg-[#27AE60]/20" data-testid={`invite-applied-${i.id}`}>
                    Applied{(i.applied_at || "").slice(0, 10) ? ` ${(i.applied_at || "").slice(0, 10)}` : ""} → view
                  </button>
                ) : (
                  <span className="ml-auto rounded-full bg-stone-100 px-2 py-0.5 font-semibold text-stone-400" data-testid={`invite-sent-${i.id}`}>Sent — not yet applied</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {settings && (
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="hiring-settings">
          <p className="font-heading text-sm font-semibold text-stone-900">Hiring settings</p>
          <p className="mt-2 rounded-lg bg-accent/50 px-3 py-2 text-xs text-stone-600" data-testid="hiring-routing-note">
            Prescreen alerts go to <b>kelly@silverhomecare.com</b> · interview notes form goes to <b>kelly@silverhomecare.com</b> (fixed routing).
          </p>
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium text-stone-500">Open positions (one per line)</label>
            <Textarea value={settings.positionsText ?? (settings.positions || []).join("\n")} onChange={(e) => setSettings((s) => ({ ...s, positionsText: e.target.value }))}
              className="min-h-16 border-stone-300 bg-white text-sm" data-testid="hiring-positions" />
          </div>
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium text-stone-500">Interview instructions (sent in the applicant's confirmation email)</label>
            <Textarea value={settings.interview_instructions} onChange={(e) => setSettings((s) => ({ ...s, interview_instructions: e.target.value }))}
              placeholder="Paste the interview instructions here — location/Zoom, what to bring, parking…"
              className="min-h-24 border-stone-300 bg-white text-sm" data-testid="hiring-instructions" />
          </div>
          <Button onClick={saveSettings} disabled={busy} className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="hiring-settings-save">
            Save settings
          </Button>
        </div>
      )}
    </div>
  );
}
